import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon, PhoneIcon, BuildingLibraryIcon } from '@heroicons/react/24/outline';
import { useSettings } from '../../../../contexts/SettingsContext';
import type { ContactInformation, BankInformation } from '../../../../types/settings';

const contactSchema = z.object({
  type: z.string().min(1, 'Type is required'),
  name: z.string().min(1, 'Label is required'),
  value: z.string().min(1, 'Value is required'),
  is_primary: z.boolean().default(false),
  is_public: z.boolean().default(true),
});

const bankSchema = z.object({
  bank_name: z.string().min(1, 'Bank name is required'),
  account_name: z.string().min(1, 'Account name is required'),
  account_number: z.string().min(1, 'Account number is required'),
  sort_code: z.string().optional(),
  swift_code: z.string().optional(),
  is_primary: z.boolean().default(false),
  is_public: z.boolean().default(true),
});

type ContactFormData = z.infer<typeof contactSchema>;
type BankFormData = z.infer<typeof bankSchema>;

const CONTACT_TYPES: Record<string, string> = {
  email: 'Email',
  phone: 'Phone',
  address: 'Address',
  social: 'Social',
  other: 'Other',
};

type Section = 'contact' | 'bank';
type View = 'list' | 'contact-form' | 'bank-form';

export default function MobileSettingsContactBanking() {
  const {
    state,
    addContactInformation, updateContactInformation, deleteContactInformation,
    addBankInformation, updateBankInformation, deleteBankInformation,
  } = useSettings();

  const [view, setView] = useState<View>('list');
  const [activeSection, setActiveSection] = useState<Section>('contact');
  const [editingContact, setEditingContact] = useState<ContactInformation | null>(null);
  const [editingBank, setEditingBank] = useState<BankInformation | null>(null);

  const contactForm = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: { type: '', name: '', value: '', is_primary: false, is_public: true },
  });

  const bankForm = useForm<BankFormData>({
    resolver: zodResolver(bankSchema),
    defaultValues: { bank_name: '', account_name: '', account_number: '', sort_code: '', swift_code: '', is_primary: false, is_public: true },
  });

  const openAddContact = () => {
    setEditingContact(null);
    contactForm.reset({ type: '', name: '', value: '', is_primary: false, is_public: true });
    setView('contact-form');
  };

  const openEditContact = (c: ContactInformation) => {
    setEditingContact(c);
    contactForm.reset({ type: c.type, name: c.name, value: c.value, is_primary: c.is_primary, is_public: c.is_public });
    setView('contact-form');
  };

  const openAddBank = () => {
    setEditingBank(null);
    bankForm.reset({ bank_name: '', account_name: '', account_number: '', sort_code: '', swift_code: '', is_primary: false, is_public: true });
    setView('bank-form');
  };

  const openEditBank = (b: BankInformation) => {
    setEditingBank(b);
    bankForm.reset({ bank_name: b.bank_name, account_name: b.account_name, account_number: b.account_number, sort_code: b.sort_code ?? '', swift_code: b.swift_code ?? '', is_primary: b.is_primary, is_public: b.is_public });
    setView('bank-form');
  };

  const onContactSubmit = async (data: ContactFormData) => {
    if (editingContact) {
      await updateContactInformation({ ...editingContact, ...data });
    } else {
      await addContactInformation(data);
    }
    setView('list');
  };

  const onBankSubmit = async (data: BankFormData) => {
    if (editingBank) {
      await updateBankInformation({ ...editingBank, ...data });
    } else {
      await addBankInformation(data);
    }
    setView('list');
  };

  if (view === 'contact-form') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-golf-gray text-base">{editingContact ? 'Edit Contact' : 'New Contact'}</h3>
          <button onClick={() => setView('list')} className="p-1 text-gray-400 hover:text-gray-600"><XMarkIcon className="w-5 h-5" /></button>
        </div>
        <form onSubmit={contactForm.handleSubmit(onContactSubmit)} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Type *</label>
            <select {...contactForm.register('type')} className="w-full text-sm rounded-xl border-gray-200 focus:border-golf-orange focus:ring-golf-orange">
              <option value="">Select type</option>
              {Object.entries(CONTACT_TYPES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {contactForm.formState.errors.type && <p className="text-xs text-red-500 mt-1">{contactForm.formState.errors.type.message}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Label *</label>
            <input type="text" {...contactForm.register('name')} placeholder="e.g. General Inquiries" className="w-full text-sm rounded-xl border-gray-200 focus:border-golf-orange focus:ring-golf-orange" />
            {contactForm.formState.errors.name && <p className="text-xs text-red-500 mt-1">{contactForm.formState.errors.name.message}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Value *</label>
            <input type="text" {...contactForm.register('value')} placeholder="e.g. info@example.com" className="w-full text-sm rounded-xl border-gray-200 focus:border-golf-orange focus:ring-golf-orange" />
            {contactForm.formState.errors.value && <p className="text-xs text-red-500 mt-1">{contactForm.formState.errors.value.message}</p>}
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" {...contactForm.register('is_primary')} className="rounded border-gray-300 text-golf-orange focus:ring-golf-orange" />
              Primary
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" {...contactForm.register('is_public')} className="rounded border-gray-300 text-golf-orange focus:ring-golf-orange" />
              Public
            </label>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setView('list')} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600">Cancel</button>
            <button type="submit" disabled={contactForm.formState.isSubmitting} className="flex-1 py-2.5 rounded-xl bg-golf-orange text-white text-sm font-medium disabled:opacity-50">
              {contactForm.formState.isSubmitting ? 'Saving...' : (editingContact ? 'Update' : 'Add')}
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (view === 'bank-form') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-golf-gray text-base">{editingBank ? 'Edit Bank Account' : 'New Bank Account'}</h3>
          <button onClick={() => setView('list')} className="p-1 text-gray-400 hover:text-gray-600"><XMarkIcon className="w-5 h-5" /></button>
        </div>
        <form onSubmit={bankForm.handleSubmit(onBankSubmit)} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Bank Name *</label>
            <input type="text" {...bankForm.register('bank_name')} className="w-full text-sm rounded-xl border-gray-200 focus:border-golf-orange focus:ring-golf-orange" />
            {bankForm.formState.errors.bank_name && <p className="text-xs text-red-500 mt-1">{bankForm.formState.errors.bank_name.message}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Account Name *</label>
            <input type="text" {...bankForm.register('account_name')} className="w-full text-sm rounded-xl border-gray-200 focus:border-golf-orange focus:ring-golf-orange" />
            {bankForm.formState.errors.account_name && <p className="text-xs text-red-500 mt-1">{bankForm.formState.errors.account_name.message}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Account Number *</label>
            <input type="text" {...bankForm.register('account_number')} className="w-full text-sm rounded-xl border-gray-200 focus:border-golf-orange focus:ring-golf-orange" />
            {bankForm.formState.errors.account_number && <p className="text-xs text-red-500 mt-1">{bankForm.formState.errors.account_number.message}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Sort Code</label>
              <input type="text" {...bankForm.register('sort_code')} className="w-full text-sm rounded-xl border-gray-200 focus:border-golf-orange focus:ring-golf-orange" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">SWIFT Code</label>
              <input type="text" {...bankForm.register('swift_code')} className="w-full text-sm rounded-xl border-gray-200 focus:border-golf-orange focus:ring-golf-orange" />
            </div>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" {...bankForm.register('is_primary')} className="rounded border-gray-300 text-golf-orange focus:ring-golf-orange" />
              Primary
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" {...bankForm.register('is_public')} className="rounded border-gray-300 text-golf-orange focus:ring-golf-orange" />
              Public
            </label>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setView('list')} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600">Cancel</button>
            <button type="submit" disabled={bankForm.formState.isSubmitting} className="flex-1 py-2.5 rounded-xl bg-golf-orange text-white text-sm font-medium disabled:opacity-50">
              {bankForm.formState.isSubmitting ? 'Saving...' : (editingBank ? 'Update' : 'Add')}
            </button>
          </div>
        </form>
      </div>
    );
  }

  const grouped = state.contactInformation.reduce((acc, c) => {
    if (!acc[c.type]) acc[c.type] = [];
    acc[c.type].push(c);
    return acc;
  }, {} as Record<string, ContactInformation[]>);

  return (
    <div className="space-y-4">
      <div className="flex rounded-xl border border-gray-200 overflow-hidden">
        <button
          onClick={() => setActiveSection('contact')}
          className={`flex-1 py-2 text-sm font-medium transition-colors ${activeSection === 'contact' ? 'bg-golf-orange text-white' : 'bg-white text-gray-600'}`}
        >
          Contact Info
        </button>
        <button
          onClick={() => setActiveSection('bank')}
          className={`flex-1 py-2 text-sm font-medium transition-colors ${activeSection === 'bank' ? 'bg-golf-orange text-white' : 'bg-white text-gray-600'}`}
        >
          Bank Details
        </button>
      </div>

      {activeSection === 'contact' && (
        <>
          <button onClick={openAddContact} className="w-full flex items-center justify-center gap-2 py-2.5 bg-golf-orange text-white rounded-xl text-sm font-medium">
            <PlusIcon className="w-4 h-4" /> Add Contact
          </button>

          {Object.keys(grouped).length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
              <PhoneIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-400">No contact information yet</p>
            </div>
          ) : (
            Object.entries(grouped).map(([type, contacts]) => (
              <div key={type} className="space-y-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-1">{CONTACT_TYPES[type] ?? type}</p>
                {contacts.map(c => (
                  <div key={c.id} className="bg-white rounded-2xl border border-gray-100 p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-golf-gray">{c.name}</p>
                        <p className="text-xs text-gray-500 truncate">{c.value}</p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {c.is_primary && <span className="text-xs bg-golf-orange/10 text-golf-orange px-2 py-0.5 rounded-full">Primary</span>}
                        {c.is_public && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Public</span>}
                      </div>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => openEditContact(c)} className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-golf-orange bg-golf-orange/10 rounded-lg">
                        <PencilIcon className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button onClick={() => deleteContactInformation(c.id)} className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-red-500 bg-red-50 rounded-lg">
                        <TrashIcon className="w-3.5 h-3.5" /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </>
      )}

      {activeSection === 'bank' && (
        <>
          <button onClick={openAddBank} className="w-full flex items-center justify-center gap-2 py-2.5 bg-golf-orange text-white rounded-xl text-sm font-medium">
            <PlusIcon className="w-4 h-4" /> Add Bank Account
          </button>

          {state.bankInformation.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
              <BuildingLibraryIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-400">No bank accounts yet</p>
            </div>
          ) : (
            state.bankInformation.map(b => (
              <div key={b.id} className="bg-white rounded-2xl border border-gray-100 p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-golf-gray">{b.bank_name}</p>
                    <p className="text-xs text-gray-500">{b.account_name}</p>
                    <p className="text-xs text-gray-400 font-mono mt-0.5">{b.account_number}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {b.is_primary && <span className="text-xs bg-golf-orange/10 text-golf-orange px-2 py-0.5 rounded-full">Primary</span>}
                  </div>
                </div>
                {(b.sort_code || b.swift_code) && (
                  <div className="flex gap-3 text-xs text-gray-400 border-t border-gray-50 pt-2 mt-2">
                    {b.sort_code && <span>Sort: {b.sort_code}</span>}
                    {b.swift_code && <span>SWIFT: {b.swift_code}</span>}
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  <button onClick={() => openEditBank(b)} className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-golf-orange bg-golf-orange/10 rounded-lg">
                    <PencilIcon className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button onClick={() => deleteBankInformation(b.id)} className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-red-500 bg-red-50 rounded-lg">
                    <TrashIcon className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
}
